import type { Dispatch, FC, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowBackIosRounded from '@mui/icons-material/ArrowBackIosRounded';
import ArrowForwardIosRounded from '@mui/icons-material/ArrowForwardIosRounded';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import type { DashboardWidgetConfig, KpiLabels } from 'src/types/dashboard';
import useGuidedTour from 'src/components/app/context/GuidedTourContext';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import ErrorOverlayWithRefetchButton from 'src/components/dashboard/DashboardPage/components/ErrorOverlayWithRefetchButton';

import BarGroup from './BarGroup';
import ChartBackground from './ChartBackground';
import GroupedBarHeaders from './GroupedBarHeaders';

import { styles } from './index.styles';

export const GROUPED_BAR_PAGE_DOWN_TEST_ID = 'grouped-bar-page-down';
export const GROUPED_BAR_PAGE_UP_TEST_ID = 'grouped-bar-page-up';
export const GROUPED_DATA_BARS_TEST_ID = 'grouped-data-bars';
export const LOADING_GROUPED_BAR_TEST_ID = 'loading-grouped-bar';

export type GroupData = [string, [number, string][], boolean];

export interface GroupedBarChartProps {
  config: DashboardWidgetConfig;
  colorMap: [string, string][];
  pageSize?: number;
  groupSize?: number;
  groupData: GroupData[];
  kpiList: KpiLabels;
  groupSelection: string[];
  setGroupSelection: Dispatch<SetStateAction<string[]>>;
  useGradient?: boolean;
  labels?: string[];
  labelTextAlign?: 'left' | 'center';
}

const GroupedBarChart: FC<GroupedBarChartProps> = ({
  config,
  colorMap,
  pageSize = 3,
  groupSize = 3,
  groupData,
  kpiList,
  groupSelection,
  setGroupSelection,
  useGradient = false,
  labels,
  labelTextAlign = 'left',
}) => {
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const { loading, errorMsg, refetch, data } = useStatistics();

  useEffect(() => {
    setGroupSelection(
      Array(groupSize)
        .fill('')
        .map((_, i) => (i === 0 ? Object.keys(kpiList)[0] || '' : '')),
    );
  }, [data]);
  const { createStep } = useGuidedTour();
  const guidedTour = useMemo(
    () => createStep(config.id, config.guided_tour_step),
    [config.guided_tour_step],
  );

  const [lastPage, fullPageGap] = useMemo(() => {
    const lastPage = (Math.ceil(groupData.length / pageSize) - 1) * pageSize;
    const pageDataRest = groupData.length % pageSize;
    const fullPageGap: number = pageDataRest ? pageSize - pageDataRest : 0;
    return [lastPage, fullPageGap];
  }, [groupData]);

  const [page, setPage] = useState<[number, number]>([
    lastPage - fullPageGap,
    lastPage + pageSize - fullPageGap,
  ]);
  const handlePageUp = useCallback(() => {
    setPage(([prevLowerEnd, prevUpperEnd]) =>
      prevUpperEnd > lastPage - fullPageGap
        ? [lastPage - fullPageGap, lastPage + pageSize - fullPageGap]
        : [prevLowerEnd + pageSize, prevUpperEnd + pageSize],
    );
  }, [lastPage, pageSize]);
  const handlePageDown = useCallback(() => {
    setPage(([prevLowerEnd, prevUpperEnd]) =>
      prevLowerEnd - pageSize < 0
        ? [0, pageSize]
        : [prevLowerEnd - pageSize, prevUpperEnd - pageSize],
    );
  }, [pageSize]);

  const paginatedGroupData = upMd
    ? groupData
    : groupData.slice(page[0], page[1]);

  if (loading || errorMsg) {
    return (
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Stack
          direction="column"
          spacing={2}
          data-test-id={LOADING_GROUPED_BAR_TEST_ID}
          sx={{ p: 2, position: 'relative' }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {config.name}
          </Typography>
          <Skeleton variant="rectangular" height={108} />
          <Skeleton variant="rectangular" height={108} />
          <Skeleton variant="rectangular" height={108} />
          <Skeleton variant="rectangular" height={108} />
        </Stack>
        {errorMsg && (
          <ErrorOverlayWithRefetchButton
            errorMsg={errorMsg}
            refetch={refetch}
            name={config.name}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, px: 2.75 }}>
      <Box sx={(theme) => styles.header(theme)}>
        <Stack direction="row" spacing={1.25} sx={{ mb: 3 }}>
          {guidedTour}
          <Stack
            direction="column"
            sx={{
              justifyContent: 'center',
            }}
          >
            <Typography variant="h5">{config.name}</Typography>
          </Stack>
        </Stack>
        <GroupedBarHeaders
          colorMap={colorMap}
          groups={kpiList}
          groupSelection={groupSelection}
          setGroupSelection={setGroupSelection}
          labels={labels}
        />
      </Box>
      <Box sx={styles.body}>
        <ChartBackground />
        <Box sx={(theme) => styles.chart(theme)}>
          <Stack direction="row" data-test-id={GROUPED_DATA_BARS_TEST_ID}>
            {paginatedGroupData.map(([label, kpiData, isLatest], i, source) => (
              <Box
                key={i}
                sx={(theme) => ({
                  width: `${100 / source.length}%`,
                  ...styles.barGroup(theme),
                })}
              >
                <BarGroup
                  kpiData={kpiData}
                  colorMap={colorMap}
                  useGradient={useGradient}
                />
                {isLatest && useGradient && (
                  <Box sx={(theme) => styles.latestMarker(theme)} />
                )}
                <Typography
                  sx={(theme) => ({
                    textAlign: labelTextAlign,
                    ...styles.labelX(isLatest && !useGradient)(theme),
                  })}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
      {!upMd && (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="squareCta"
            color="secondary"
            disabled={page[0] <= 0}
            onClick={handlePageDown}
            data-test-id={GROUPED_BAR_PAGE_DOWN_TEST_ID}
          >
            <ArrowBackIosRounded />
          </Button>
          <Button
            variant="squareCta"
            color="secondary"
            disabled={page[1] > lastPage}
            onClick={handlePageUp}
            data-test-id={GROUPED_BAR_PAGE_UP_TEST_ID}
          >
            <ArrowForwardIosRounded />
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default GroupedBarChart;
